import { DatasetType, Data } from '../types'
import AuthScope from '../../../utils/api/authScope'
import { DataSubtype, DataType } from '../../../../pages/SearchPage/types'

export type AttributeFunction = (fields: Data) => string | undefined | Array<string | undefined>

export interface DataSelectionConfig {
  authScope?: AuthScope
  dataType: DataType
  dataSubtype: DataSubtype
  columns: Array<{
    label: string
    attribute: keyof Data | AttributeFunction
  }>
  downloadFields: string[]
  filters: Array<{
    slug: string
    label: string
    order?: string[]
    info_url?: string
    formatter?: string
  }>
  title: string
  sort?: string
}

const jsonField = (json: string | undefined, key: string): string | undefined => {
  try {
    return JSON.parse(json ?? '')[key]
  } catch {
    return undefined
  }
}

const DATA_SELECTION_CONFIG: Record<DatasetType, DataSelectionConfig> = {
  [DatasetType.Bag]: {
    dataType: DataType.Bag,
    dataSubtype: DataSubtype.Nummeraanduiding,
    columns: [
      { label: 'Naam openbare ruimte', attribute: 'openbareruimteNaam' },
      { label: 'Num.', attribute: 'huisnummer' },
      { label: 'Let.', attribute: 'huisletter' },
      { label: 'Toev.', attribute: 'huisnummertoevoeging' },
      { label: 'Postcode', attribute: 'postcode' },
      { label: 'Woonplaats', attribute: 'woonplaatsNaam' },
      { label: 'Gemeente', attribute: 'brkGemeenteNaam' },
      { label: 'Type adres', attribute: 'typeAdres' },
      {
        label: 'Gebr.doel vot',
        attribute: ({ gebruiksdoelOmschrijvingen }) => gebruiksdoelOmschrijvingen?.join(', '),
      },
      { label: 'Status vot', attribute: 'verblijfsobjectStatusOmschrijving' },
      { label: 'Opp. vot', attribute: 'verblijfsobjectOppervlakte' },
      {
        label: 'Bouwjaar pand',
        attribute: ({ panden }) =>
          Array.isArray(panden)
            ? panden?.map((pand) => jsonField(pand, 'bouwjaar Pand'))?.join(', ')
            : undefined,
      },
      {
        label: 'Status pand',
        attribute: ({ panden }) =>
          Array.isArray(panden)
            ? panden?.map((pand) => jsonField(pand, 'status Pand'))?.join(', ')
            : undefined,
      },
      { label: 'Stadsdeel', attribute: 'gebiedenStadsdeelNaam' },
      { label: 'GGW-gebied', attribute: 'gebiedenGgwgebiedNaam' },
      { label: 'Wijk', attribute: 'gebiedenWijkNaam' },
      { label: 'Buurt', attribute: 'gebiedenBuurtNaam' },
    ],
    downloadFields: [
      'openbareruimteNaam',
      'huisnummer',
      'huisletter',
      'huisnummertoevoeging',
      'postcode',
      'woonplaatsNaam',
      'brkGemeenteNaam',
      'typeAdres',
      'gebruiksdoelOmschrijvingen',
      'verblijfsobjectGebruiksdoelWoonfunctieOmschrijving',
      'verblijfsobjectGebruiksdoelGezondheidszorgfunctieOmschrijving',
      'verblijfsobjectAantalEenhedenComplex',
      'verblijfsobjectAantalKamers',
      'toegangOmschrijvingen',
      'verblijfsobjectVerdiepingToegang',
      'verblijfsobjectAantalBouwlagen',
      'verblijfsobjectHoogsteBouwlaag',
      'verblijfsobjectLaagsteBouwlaag',
      'verblijfsobjectOppervlakte',
      'verblijfsobjectStatusOmschrijving',
      'verblijfsobjectGeconstateerd',
      'kenmerkenInOnderzoek',
      'verblijfsobjectDocumentnummer',
      'verblijfsobjectDocumentdatum',
      'verblijfsobjectBagproces',
      // Oorspronkelijk bouwjaar
      // Naam pand
      // Type woonobject
      // Ligging
      // Aantal bouwlagen pand
      // Hoogste bouwlaag pand
      // Laagste bouwlaag pand
      // BAG-proces pand
      // Geconstateerd pand
      // Status pand
      'typeAdresseerbaarObjectOmschrijving',
      'openbareruimteIdentificatie',
      // Pand identificatie
      'verblijfsobjectIdentificatie',
      'ligplaatsIdentificatie',
      'ligplaatsGebruiksdoelOmschrijvingen',
      'standplaatsIdentificatie',
      'standplaatsGebruiksdoelOmschrijvingen',
      'identificatie',
      'wozSoortObjectOmschrijving',
      'gebiedenStadsdeelNaam',
      'gebiedenStadsdeelCode',
      'gebiedenGgwgebiedNaam',
      'gebiedenGgwgebiedCode',
      'gebiedenWijkNaam',
      'gebiedenWijkCode',
      'gebiedenBuurtNaam',
      'gebiedenBuurtCode',
      // Code bouwblok
      'adresseerbaarObjectPuntGeometrie',
      'adresseerbaarObjectPuntGeometrieWgs84',
    ],
    filters: [
      {
        slug: 'woonplaatsNaam',
        label: 'Woonplaats',
      },
      {
        slug: 'gebiedenStadsdeelNaam',
        label: 'Stadsdeel',
      },
      {
        slug: 'gebiedenGgwgebiedNaam',
        label: 'GGW-gebied',
      },
      {
        slug: 'gebiedenWijkNaam',
        label: 'Wijk',
      },
      {
        slug: 'gebiedenBuurtNaam',
        label: 'Buurt',
      },
      {
        slug: 'openbareruimteNaam',
        label: 'Openbare ruimte',
      },
      {
        slug: 'postcode',
        label: 'Postcode',
      },
    ],
    sort: 'openbareruimteNaam,huisnummer,huisletter,huisnummertoevoeging',
    title: 'Adressen',
  },
  [DatasetType.Brk]: {
    authScope: AuthScope.BrkRsn,
    dataType: DataType.BRK,
    dataSubtype: DataSubtype.Object,
    columns: [],
    downloadFields: [],
    filters: [
      {
        slug: 'eigenaar_type',
        label: 'Type zakelijk gerechtigde',
        order: ['Grondeigenaar', 'Pandeigenaar', 'Appartementseigenaar'],
        info_url: 'https://www.amsterdam.nl/stelselpedia/brk-index/producten-brk/kadastertabel/',
      },
      {
        slug: 'eigenaar_cat',
        label: 'Zakelijk gerechtigde',
        order: [
          'Gemeente Amsterdam',
          'Overige gemeenten',
          'Staat',
          'Provincies',
          'Waterschappen',
          'Woningbouwcorporaties',
          'Verenigingen van eigenaren',
          'Spoorwegen/ProRail',
          'Overige niet-natuurlijke personen',
          'Overige natuurlijke personen',
        ],
        info_url: 'https://www.amsterdam.nl/stelselpedia/brk-index/producten-brk/kadastertabel/',
      },
      {
        slug: 'woonplaats',
        label: 'Woonplaats',
      },
      {
        slug: 'stadsdeel_naam',
        label: 'Stadsdeel',
      },
      {
        slug: 'ggw_naam',
        label: 'GGW-gebied',
      },
      {
        slug: 'buurtcombinatie_naam',
        label: 'Wijk',
      },
      {
        slug: 'buurt_naam',
        label: 'Buurt',
      },
    ],
    title: 'Kadastrale objecten met zakelijk rechthebbenden',
  },
  [DatasetType.Hr]: {
    authScope: AuthScope.HrR,
    dataType: DataType.HR,
    dataSubtype: DataSubtype.Vestigingen,
    columns: [],
    downloadFields: [],
    filters: [
      {
        slug: 'sbi_code',
        label: 'SBI-code',
      },
      {
        slug: 'sbi_l2',
        label: 'SBI-L2',
      },
      {
        slug: 'sbi_l3',
        label: 'SBI-L3',
      },
      {
        slug: 'sbi_l4',
        label: 'SBI-L4',
      },
      {
        slug: 'sbi_l5',
        label: 'SBI-L5',
      },
      {
        slug: 'bijzondere_rechtstoestand',
        label: 'Bijzondere rechtstoestand',
      },
      {
        slug: 'woonplaats',
        label: 'Woonplaats',
      },
      {
        slug: 'stadsdeel_naam',
        label: 'Stadsdeel',
      },
      {
        slug: 'ggw_naam',
        label: 'GGW-gebied',
      },
      {
        slug: 'buurtcombinatie_naam',
        label: 'Wijk',
      },
      {
        slug: 'buurt_naam',
        label: 'Buurt',
      },
      {
        slug: 'openbare_ruimte',
        label: 'Openbare ruimte',
      },
      {
        slug: 'postcode',
        label: 'Postcode',
      },
    ],
    title: 'Vestigingen',
  },
}

export default DATA_SELECTION_CONFIG
